import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import './BarRestaurantOrderCardCustom.css';
import AppIcon from './icons/AppIcon';

export default function BarRestaurantOrderCardCustom({ order, onOpenCard, onOpenTable, isDragging }) {
  const [localOrder, setLocalOrder] = useState(order);
  const [timeElapsed, setTimeElapsed] = useState('0m');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const calculateTimeElapsed = (timestamp) => {
    if (!timestamp) return '0m';
    
    const agora = new Date();
    const entrada = new Date(timestamp);
    const diffMs = agora - entrada;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays}d ${diffHours % 24}h`;
    } else if (diffHours > 0) {
      return `${diffHours}h ${diffMins % 60}m`;
    } else if (diffMins > 0) {
      return `${diffMins}m`;
    } else {
      return `${diffSecs}s`;
    }
  };

  useEffect(() => {
    setLocalOrder(order);
  }, [order]);

  useEffect(() => {
    if (!localOrder?.timestamp_entrada_lista) return;

    setTimeElapsed(calculateTimeElapsed(localOrder.timestamp_entrada_lista));
    const interval = setInterval(() => {
      setTimeElapsed(calculateTimeElapsed(localOrder.timestamp_entrada_lista));
    }, 1000);

    return () => clearInterval(interval);
  }, [localOrder?.timestamp_entrada_lista]);

  const handleStatusChange = async (newStatus) => {
    try {
      setUpdatingStatus(true);
      const agora = new Date().toISOString();
      
      // ✅ Validar status
      const statusValidos = ['pedidos_em_aberto', 'em_producao', 'pronto', 'entregue', 'cancelado'];
      if (!statusValidos.includes(newStatus)) {
        console.error('[BarRestaurantOrderCardCustom] Status inválido:', newStatus);
        alert('Status inválido');
        setUpdatingStatus(false);
        return;
      }

      console.log('[BarRestaurantOrderCardCustom] Atualizando status:', {
        orderId: localOrder.id,
        statusAtual: localOrder?.status_atual,
        novoStatus: newStatus,
        timestamp: agora
      });

      const { error, data } = await supabase
        .from('bar_restaurant_orders')
        .update({
          status_atual: newStatus,
          timestamp_entrada_lista: agora,
          historico_movimentacoes: [
            ...(localOrder?.historico_movimentacoes || []),
            {
              de: localOrder?.status_atual,
              para: newStatus,
              timestamp: agora
            }
          ]
        })
        .eq('id', localOrder.id);

      if (error) {
        console.error('[BarRestaurantOrderCardCustom] Erro Supabase:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        });
        throw error;
      }

      console.log('[BarRestaurantOrderCardCustom] ✅ Status atualizado com sucesso:', newStatus);

      setLocalOrder(prev => ({
        ...prev,
        status_atual: newStatus,
        timestamp_entrada_lista: agora
      }));

      setShowStatusDropdown(false);
    } catch (error) {
      console.error('[BarRestaurantOrderCardCustom] ❌ Erro ao atualizar status:', error.message);
      alert(`Erro ao atualizar status do pedido: ${error.message}`);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusBadgeColor = (status) => {
    const statusMap = {
      'confirmado': 'confirmed',
      'em_producao': 'in_production',
      'pronto': 'ready',
      'entregue': 'delivered',
      'cancelado': 'cancelled'
    };
    return statusMap[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'pedidos_em_aberto': 'Pedidos em aberto',
      'em_producao': '⏳ Em Produção',
      'pronto': '✓ Pronto',
      'entregue': '✓ Entregue',
      'cancelado': '✗ Cancelado'
    };
    return statusMap[status] || status;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const getElapsedTimeClass = (elapsed) => {
    if (!elapsed) return 'time-green';
    
    let totalMinutes = 0;
    
    // Extrair dias (d)
    const daysMatch = elapsed.match(/(\d+)d/);
    if (daysMatch) {
      totalMinutes += parseInt(daysMatch[1]) * 24 * 60;
    }
    
    // Extrair horas (h)
    const hoursMatch = elapsed.match(/(\d+)h/);
    if (hoursMatch) {
      totalMinutes += parseInt(hoursMatch[1]) * 60;
    }
    
    // Extrair minutos (m)
    const minutesMatch = elapsed.match(/(\d+)m/);
    if (minutesMatch) {
      totalMinutes += parseInt(minutesMatch[1]);
    }
    
    // Extrair segundos (s)
    const secondsMatch = elapsed.match(/(\d+)s/);
    if (secondsMatch) {
      totalMinutes += parseInt(secondsMatch[1]) / 60;
    }

    if (totalMinutes <= 10) return 'time-green';
    if (totalMinutes <= 20) return 'time-yellow';
    return 'time-red';
  };

  if (!localOrder) {
    return null;
  }

  const orderPayload = localOrder?.order_payload || {};
  const statusAtual = localOrder?.status_atual || 'pendente';
  const itensDetalhados = orderPayload?.itens || [];
  const timestampEntrada = localOrder?.timestamp_entrada_lista || localOrder?.created_at;

  return (
    <div 
      className="bar-restaurant-order-card-custom" 
      onClick={() => onOpenCard && onOpenCard(localOrder)}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      {/* Header com Mesa e Total */}
      <div className="order-header">
        <div className="order-mesa">
          <AppIcon name="table_restaurant" />
          <span className="mesa-number">Mesa {orderPayload.mesa || '?'}</span>
        </div>
        <div className="order-total">
          <span className="total-value">R$ {(orderPayload.total || 0).toFixed(2)}</span>
        </div>
      </div>

      {/* Lista de Itens */}
      {itensDetalhados.length > 0 && (
        <div className="order-items">
          {itensDetalhados.map((item, idx) => (
            <div key={idx} className="order-item">
              <div className="item-name">
                <span className="item-qty">x{item.quantidade}</span>
                <span className="item-title">{item.nome}</span>
              </div>
              <div className="item-price">
                R$ {item.preco_total?.toFixed(2) || '0.00'}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer com Status e Timestamp */}
      <div className="order-footer">
        <div className="status-section">
          <div className={`order-status ${getStatusBadgeColor(statusAtual)}`}>
            {getStatusLabel(statusAtual)}
          </div>
          <div className="status-dropdown-wrapper">
            <button
              className="btn-change-status"
              onClick={(e) => {
                e.stopPropagation();
                setShowStatusDropdown(!showStatusDropdown);
              }}
              title="Mudar status"
              disabled={updatingStatus}
            >
              <AppIcon name="swap_vert" />
            </button>
            {showStatusDropdown && (
              <div className="status-dropdown-menu">
                <button
                  className="status-option"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStatusChange('pedidos_em_aberto');
                  }}
                  disabled={statusAtual === 'pedidos_em_aberto' || updatingStatus}
                >
                  Pedidos em aberto
                </button>
                <button
                  className="status-option"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStatusChange('em_producao');
                  }}
                  disabled={statusAtual === 'em_producao' || updatingStatus}
                >
                  Em produção
                </button>
                <button
                  className="status-option"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStatusChange('pronto');
                  }}
                  disabled={statusAtual === 'pronto' || updatingStatus}
                >
                  Pronto
                </button>
                <button
                  className="status-option"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStatusChange('entregue');
                  }}
                  disabled={statusAtual === 'entregue' || updatingStatus}
                >
                  Entregue
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="order-time-group">
          <div className="order-time">
            <AppIcon name="schedule" />
            <span>{formatTime(timestampEntrada)}</span>
          </div>
          <div className={`order-elapsed-time ${getElapsedTimeClass(timeElapsed)}`}>
            <AppIcon name="timer" />
            <span>{timeElapsed}</span>
          </div>
          <button
            className="btn-view-table"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (onOpenTable) {
                onOpenTable(localOrder);
              }
            }}
          >
            <AppIcon name="table_restaurant" />
            Ver mesa
          </button>
        </div>
      </div>

      {/* Indicador de quantidade de itens */}
      {itensDetalhados.length > 0 && (
        <div className="order-item-count">
          {itensDetalhados.length} item{itensDetalhados.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}
